import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from '@cotera/client/v0/lib/utils';
import { useAnalytics } from '@cotera/client/app/etc';
import { Icon, IconName } from '@cotera/client/app/components/ui';

const buttonVariants = cva(
  "cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive active:scale-[0.95]",
  {
    variants: {
      variant: {
        active:
          'bg-primary text-primary-foreground shadow-xs hover:bg-primary/80 active:bg-primary/90',
        default:
          'bg-primary text-primary-foreground shadow-xs hover:bg-primary/80 active:bg-primary/90',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        'ghost-destructive': 'text-destructive/80 hover:text-destructive',
        outline:
          'border border-border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost:
          'hover:bg-accent/70 hover:text-accent-foreground dark:hover:bg-accent/50 active:bg-accent',
        link: 'text-primary underline-offset-4 hover:underline',
        primary:
          'bg-white text-indigo-600 shadow-xs border border-indigo-300 hover:border-indigo-400 active:border-indigo-500 transition-all duration-200',
      },
      size: {
        md: 'h-8 px-4 py-2 has-[>svg]:px-3',
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-sm gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-sm px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-md': 'size-8',
        'icon-sm': 'size-6',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  icon,
  className,
  variant,
  size,
  asChild = false,
  posthogEvent,
  loading = false,
  children,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    posthogEvent?: string;
    icon?: IconName;
    loading?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';
  const analytics = useAnalytics();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (loading) {
      e.preventDefault();
      return;
    }

    // Track PostHog event if manually provided
    // Note: Action-based events are now tracked automatically by useAction hook
    if (posthogEvent) {
      analytics.track(posthogEvent);
    }

    // Call original onClick handler if provided
    if (props.onClick) {
      props.onClick(e);
    }
  };

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
      type={props.type ?? 'button'}
      disabled={props.disabled === true || loading}
      aria-busy={loading}
      onClick={handleClick}
    >
      {loading ? (
        <Loader2 className="animate-spin" />
      ) : (
        icon !== undefined && <Icon icon={icon} />
      )}
      {children}
    </Comp>
  );
}

export const ButtonGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    variant?: VariantProps<typeof buttonVariants>['variant'];
    size?: VariantProps<typeof buttonVariants>['size'];
    orientation?: 'horizontal' | 'vertical';
  }
>(
  (
    {
      variant: _variant,
      size: _size,
      className: _className,
      orientation,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(orientation === 'horizontal' ? 'flex-row' : 'flex-col')}
        {...props}
      />
    );
  }
);

export { Button, buttonVariants };
